import React, { useState, useEffect } from "react";
import {
  ModuleAccordionGroup,
  MaterialIcon
} from "@loanbossinc/component-library";
import { AccordionSection } from "component-lib/AccordionSection";

import { LOAN_SECTION_KEYS } from "../loan.constants";
import { findSummaryText } from "../loanDetailEditUtil";

import {
  BORROWER_GROUP,
  GUARANTOR_GROUP,
  CASHMANAGEMENT_GROUP
} from "./SampleGroups";
import FinancialReportingRow from "./FinancialReportingRow";
import { rows as sectionCashManagement } from "./section-loan-cash-management-rows";
import { GroupWork } from "@material-ui/icons";

const nullData = {
  stylename: "Work Sans /  16 Regular",
  fontFamily: "Work Sans",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19px",
  letterSpacing: "0em",
  textAlign: "center",
  textColor: "#7d8084"
};
const iconText = {
  fontSize: "12px",
  textColor: "#7d8084",
  margin: "10px"
};
const DeliverableTypes = {
  borrower: "borrower",
  guarantor: "guarantor",
  cashManagement: "cashManagement"
};
const ReportingFrequencyNames = {
  "bbd95d5d-4688-11e9-82f6-0242ac120002": "Monthly",
  "bf12a607-4688-11e9-82f6-0242ac120002": "Bi-Monthly",
  "d019a5e2-4688-11e9-82f6-0242ac120002": "Quarterly",
  "d41fb593-4688-11e9-82f6-0242ac120002": "Semi-Annually",
  "d749ecb6-4688-11e9-82f6-0242ac120002": "Annually",
  "eefe2b88-4688-11e9-82f6-0242ac120002": "None"
};

const reportingFrequencyOrder = [
  "Monthly",
  "Quarterly",
  "Semi-Annually",
  "Annually"
];

const FinancialReporting = ({
  data,
  eventHandlers,
  history,
  loanId,
  modifySummaryText,
  ...other
}) => {
  const [groups, setGroups] = useState([]);
  const [headerGroup, setHeaderGroup] = useState(null);
  const [borrowerGroup, setBorrowerGroup] = useState(BORROWER_GROUP);
  const [guarantorGroup, setGuarantorGroup] = useState(GUARANTOR_GROUP);
  const [cashManagementGroup, setCashManagementGroup] = useState(
    CASHMANAGEMENT_GROUP
  );

  const generateAddButton = (key, reportType, newIndex) => ({
    isButton: true,
    key,
    buttonLabel: "Add new report packet",
    showEditButton: true,
    onClick: () => history.push(`/loans/${loanId}/edit/${reportType}/new/${newIndex}`)
  });

  const getSubTitle = data => {
    const priorityHigh = data.covenants ? data.covenants.highPriority : null;
    if (priorityHigh) {
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <MaterialIcon iconName="error_outline" className="material-icons" />
          <span style={{ width: "100%", marginLeft: "8px" }}>
            {" "}
            This loan is in cash management
          </span>
        </div>
      );
    }

    return null;
  };

  const drawDataCashManagement = data => {
    if (data.covenants) {
      if (
        data.covenants.cashManagementDeliveryPeriod ||
        data.covenants.reportingStartDate ||
        data.covenants.cashManagementListOfDocs ||
        data.covenants.cashManagementReportingMisc ||
        data.covenants.reportingWaiverStartDate ||
        data.covenants.reportingWaiverEndDate
      ) {
        return (
          <AccordionSection
            eventHandlers={eventHandlers}
            rows={sectionCashManagement}
            data={data}
          />
        );
      }

      return (
        <p style={nullData}>Data has not been entered for this section.</p>
      );
    }

    return (
      <p style={nullData}>Data has not been entered for this section.</p>
    );
  };

  const generateCMGeneralSection = data => ({
    title: "General",
    subTitle: getSubTitle(data),
    key: LOAN_SECTION_KEYS.FINANCIAL_REPORTING_GENERAL,
    isExpandable: true,
    buttonLabel: "Edit Details",
    showEditButton: true,
    onClick: () => history.push(
      `/loans/${data.details.id}/edit/${LOAN_SECTION_KEYS.FINANCIAL_REPORTING_GENERAL}`
    ),
    sections: [drawDataCashManagement(data)],
    eventHandlers,
    references: data.references,
    referencedFieldPath: "loans.cashmanagementreporting.header",
    referenceStyles: { marginLeft: "20px" }
  });

  const generateReportingRows = (
    reports,
    keyName,
    reportType,
    deliverableType
  ) => {
    const sortedReports = sortReports(reports);

    return sortedReports?.map((report, index) => ({
      title: ReportingFrequencyNames[report?.reportingFrequencyId],
      sortOrder: index,
      key: `${keyName}-reporting-${index}`,
      isTitleGroup: false,
      isExpandable: true,
      buttonLabel: "Edit Details",
      showEditButton: true,
      onClick: () => history.push(`/loans/${loanId}/edit/${keyName}/${report.id}`),
      sections: [
        <AccordionSection
          key={index}
          data={report}
          eventHandlers={eventHandlers}
          rows={FinancialReportingRow(deliverableType)}
        />
      ]
    }));
  };

  const sortReports = reports => reports.sort((reportA, reportB) => (returnIndexOfFrequency(reportA, ReportingFrequencyNames) >
      returnIndexOfFrequency(reportB, ReportingFrequencyNames) ?
    1 :
    -1));

  const returnIndexOfFrequency = (report, frequencyNames) => reportingFrequencyOrder.indexOf(
    frequencyNames[report?.reportingFrequencyId]
  );

  const createGroup = (group) => ({
    ...group,
    title: group.title,
    key: `${group.title}-reporting`,
    isTitleGroup: false,
    isExpandable: false,
    references: data.references,
    referencedFieldPath: group.referencedFieldPath,
    referenceStyles: group.referenceStyles,
    isReferenceOptions: group.isReferenceOptions,
    eventHandlers,
  });

  const findGroupsByDeliverableTypes = deliverableType => {
    const { deliverables = [] } = data || {};

    return deliverables.filter(
      deliverable => deliverable.deliverableType === deliverableType
    );
  };

  const SHORTHAND_FIELD = "FinancialReporting";
  useEffect(() => {
    const summaryText = findSummaryText(data, SHORTHAND_FIELD);
    const HEADER_GROUP = {
      title: "Financial Reporting",
      key: LOAN_SECTION_KEYS.FINANCIAL_REPORTING,
      isTitleGroup: true,
      isExpandable: false,
      hasSummaryText: true,
      summaryText,
      modifySummaryText: () => modifySummaryText(SHORTHAND_FIELD, summaryText)
    };

    setHeaderGroup(HEADER_GROUP);
    const newBorrowerGroup = createGroup(borrowerGroup);
    setBorrowerGroup(newBorrowerGroup);
    const newGuarantorGroup = createGroup(guarantorGroup);
    setGuarantorGroup(newGuarantorGroup);
    const newCashManagementGroup = createGroup(cashManagementGroup);
    setCashManagementGroup(newCashManagementGroup);
  }, [data]);

  useEffect(() => {
    const borrowerSectionsFromDeliverables = findGroupsByDeliverableTypes(
      DeliverableTypes.borrower
    );
    const guarantorSectionsFromDeliverables = findGroupsByDeliverableTypes(
      DeliverableTypes.guarantor
    );
    const cashManagementSectionsFromDeliverables = findGroupsByDeliverableTypes(
      DeliverableTypes.cashManagement
    );

    const borrowerReports = generateReportingRows(
      borrowerSectionsFromDeliverables,
      LOAN_SECTION_KEYS.BORROWERREPORTING,
      "Borrower",
      "borrower"
    );
    const guarantorReports = generateReportingRows(
      guarantorSectionsFromDeliverables,
      LOAN_SECTION_KEYS.GUARANTORREPORTING,
      "Guarantor",
      "guarantor"
    );
    const cashManagementReports = generateReportingRows(
      cashManagementSectionsFromDeliverables,
      LOAN_SECTION_KEYS.CASHMANAGEMENTREPORTING,
      "Cash Management",
      "cashManagement"
    );
    const newItemIndex = data.deliverables ? data.deliverables.length : 0;

    const groups = [];

    if (headerGroup) {
      groups.push(headerGroup);
    }

    if (borrowerGroup) {
      groups.push(borrowerGroup);
    }
    if (borrowerReports && borrowerReports.length > 0) {
      groups.push(...borrowerReports);
    }
    groups.push(
      generateAddButton(
        LOAN_SECTION_KEYS.ADDBORROWERREPORTINGBUTTON,
        LOAN_SECTION_KEYS.BORROWERREPORTING,
        newItemIndex
      )
    );

    if (guarantorGroup) {
      groups.push(guarantorGroup);
    }
    if (guarantorReports && guarantorReports.length > 0) {
      groups.push(...guarantorReports);
    }
    groups.push(
      generateAddButton(
        LOAN_SECTION_KEYS.ADDGUARANTORREPORTINGBUTTON,
        LOAN_SECTION_KEYS.GUARANTORREPORTING,
        newItemIndex
      )
    );

    if (cashManagementGroup) {
      groups.push(cashManagementGroup);
    }
    groups.push(generateCMGeneralSection(data));
    if (cashManagementReports && cashManagementReports.length > 0) {
      groups.push(...cashManagementReports);
    }
    groups.push(
      generateAddButton(
        LOAN_SECTION_KEYS.ADDCASHMANAGEMENTREPORTINGBUTTON,
        LOAN_SECTION_KEYS.CASHMANAGEMENTREPORTING,
        newItemIndex
      )
    );

    setGroups(groups);
  }, [borrowerGroup, guarantorGroup, cashManagementGroup]);

  return <ModuleAccordionGroup {...other} groups={groups} />;
};

export default FinancialReporting;
